import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  usePortalPermissions,
  type MessageTemplate,
  type MessageTemplateChannel,
} from '@ubus/shared';
import AdminListPage from '../components/AdminListPage';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const KEY_PATTERN = /^[a-z][a-z0-9_.]*$/;

function variablesList(row: MessageTemplate): string[] {
  const v = row.variables;
  if (Array.isArray(v)) return v.map(String);
  return [];
}

function normalizeVariables(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((x) => String(x).trim()).filter(Boolean))];
}

type DrawerMode = 'create' | 'edit';

function SystemConfig() {
  const { t } = useTranslation();
  const { hasPermission } = usePortalPermissions();
  const canEdit = hasPermission('system_config.edit');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<MessageTemplate[]>([]);
  const [channelFilter, setChannelFilter] = useState<MessageTemplateChannel | 'all'>('all');
  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);
  const [editing, setEditing] = useState<MessageTemplate | null>(null);
  const [form] = Form.useForm();
  const watchChannel = Form.useWatch('channel', form) as MessageTemplateChannel | undefined;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .order('channel', { ascending: true })
        .order('template_key', { ascending: true });
      if (error) throw error;
      setRows((data ?? []) as MessageTemplate[]);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t('systemConfig.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (channelFilter === 'all') return rows;
    return rows.filter((r) => r.channel === channelFilter);
  }, [rows, channelFilter]);

  const closeDrawer = () => {
    setDrawerMode(null);
    setEditing(null);
    form.resetFields();
  };

  const openCreate = () => {
    setEditing(null);
    setDrawerMode('create');
    form.setFieldsValue({
      template_key: '',
      channel: 'sms',
      locale: 'en',
      name: '',
      subject: '',
      body: '',
      variables: [],
      enabled: true,
    });
  };

  const openEdit = (row: MessageTemplate) => {
    setEditing(row);
    setDrawerMode('edit');
    form.setFieldsValue({
      template_key: row.template_key,
      channel: row.channel,
      locale: row.locale,
      name: row.name,
      subject: row.subject ?? '',
      body: row.body,
      variables: variablesList(row),
      enabled: row.enabled,
    });
  };

  const handleSave = async () => {
    if (!canEdit || !drawerMode) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const channel = (values.channel ?? editing?.channel) as MessageTemplateChannel;
      const subject =
        channel === 'email' ? String(values.subject ?? '').trim() : null;
      const variables = normalizeVariables(values.variables);
      const now = new Date().toISOString();

      if (drawerMode === 'create') {
        const { error } = await supabase.from('message_templates').insert({
          template_key: String(values.template_key ?? '').trim(),
          channel,
          locale: String(values.locale ?? 'en').trim() || 'en',
          name: String(values.name ?? '').trim(),
          subject,
          body: String(values.body ?? ''),
          variables,
          enabled: Boolean(values.enabled),
          updated_at: now,
        });
        if (error) {
          if (error.code === '23505') {
            throw new Error(t('systemConfig.duplicateKey'));
          }
          throw error;
        }
        message.success(t('systemConfig.created'));
      } else if (editing) {
        const { error } = await supabase
          .from('message_templates')
          .update({
            name: String(values.name ?? '').trim(),
            subject,
            body: String(values.body ?? ''),
            variables,
            enabled: Boolean(values.enabled),
            updated_at: now,
          })
          .eq('id', editing.id);
        if (error) throw error;
        message.success(t('systemConfig.saved'));
      }

      closeDrawer();
      await load();
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error(err instanceof Error ? err.message : t('systemConfig.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const activeChannel: MessageTemplateChannel =
    drawerMode === 'create'
      ? watchChannel === 'email'
        ? 'email'
        : 'sms'
      : editing?.channel ?? 'sms';

  const columns: TableProps<MessageTemplate>['columns'] = [
    {
      title: t('systemConfig.colName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('systemConfig.colKey'),
      dataIndex: 'template_key',
      key: 'template_key',
      width: 300,
      ellipsis: true,
      render: (v: string) => (
        <Text code style={{ whiteSpace: 'nowrap' }} title={v}>
          {v}
        </Text>
      ),
    },
    {
      title: t('systemConfig.colChannel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (ch: MessageTemplateChannel) => (
        <Tag color={ch === 'email' ? 'blue' : 'green'}>
          {ch === 'email' ? t('systemConfig.channelEmail') : t('systemConfig.channelSms')}
        </Tag>
      ),
    },
    {
      title: t('systemConfig.colLocale'),
      dataIndex: 'locale',
      key: 'locale',
      width: 80,
    },
    {
      title: t('systemConfig.colEnabled'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: 90,
      render: (on: boolean) => (
        <Tag color={on ? 'success' : 'default'}>
          {on ? t('systemConfig.enabledYes') : t('systemConfig.enabledNo')}
        </Tag>
      ),
    },
    {
      title: t('systemConfig.colUpdated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => openEdit(row)}>
          {canEdit ? t('systemConfig.edit') : t('systemConfig.view')}
        </Button>
      ),
    },
  ];

  return (
    <AdminListPage
      title={t('systemConfig.title')}
      subtitle={t('systemConfig.subtitle')}
      resultCount={filtered.length}
      extra={
        canEdit ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('systemConfig.create')}
          </Button>
        ) : null
      }
      alerts={
        <Alert type="info" showIcon message={t('systemConfig.notWiredHint')} style={{ marginBottom: 0 }} />
      }
      filters={
        <Select
          style={{ width: 160 }}
          value={channelFilter}
          onChange={(v) => setChannelFilter(v)}
          options={[
            { value: 'all', label: t('systemConfig.filterAll') },
            { value: 'sms', label: t('systemConfig.channelSms') },
            { value: 'email', label: t('systemConfig.channelEmail') },
          ]}
        />
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 1040 }}
      />

      <Drawer
        title={
          drawerMode === 'create'
            ? t('systemConfig.createTitle')
            : editing
              ? editing.name
              : t('systemConfig.edit')
        }
        open={drawerMode !== null}
        onClose={closeDrawer}
        width={560}
        destroyOnClose
        extra={
          canEdit ? (
            <Space>
              <Button onClick={closeDrawer}>{t('common.cancel')}</Button>
              <Button type="primary" loading={saving} onClick={() => void handleSave()}>
                {drawerMode === 'create' ? t('systemConfig.create') : t('systemConfig.save')}
              </Button>
            </Space>
          ) : null
        }
      >
        {drawerMode ? (
          <>
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
              {t('systemConfig.variablesHint')}
            </Paragraph>
            <Form form={form} layout="vertical" disabled={!canEdit}>
              {drawerMode === 'create' ? (
                <>
                  <Form.Item
                    name="template_key"
                    label={t('systemConfig.colKey')}
                    rules={[
                      { required: true, message: t('systemConfig.keyRequired') },
                      {
                        pattern: KEY_PATTERN,
                        message: t('systemConfig.keyInvalid'),
                      },
                    ]}
                    extra={t('systemConfig.keyHint')}
                  >
                    <Input placeholder="e.g. trip.cancelled.sms" />
                  </Form.Item>
                  <Form.Item
                    name="channel"
                    label={t('systemConfig.colChannel')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        { value: 'sms', label: t('systemConfig.channelSms') },
                        { value: 'email', label: t('systemConfig.channelEmail') },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    name="locale"
                    label={t('systemConfig.colLocale')}
                    rules={[{ required: true, message: t('systemConfig.localeRequired') }]}
                    extra={t('systemConfig.localeHint')}
                  >
                    <Input placeholder="en" />
                  </Form.Item>
                </>
              ) : editing ? (
                <Paragraph type="secondary">
                  <Text code>{editing.template_key}</Text>
                  {' · '}
                  {editing.channel === 'email'
                    ? t('systemConfig.channelEmail')
                    : t('systemConfig.channelSms')}
                  {' · '}
                  {editing.locale}
                </Paragraph>
              ) : null}

              <Form.Item
                name="name"
                label={t('systemConfig.colName')}
                rules={[{ required: true, message: t('systemConfig.nameRequired') }]}
              >
                <Input />
              </Form.Item>
              {activeChannel === 'email' ? (
                <Form.Item
                  name="subject"
                  label={t('systemConfig.subject')}
                  rules={[{ required: true, message: t('systemConfig.subjectRequired') }]}
                >
                  <Input />
                </Form.Item>
              ) : null}
              <Form.Item
                name="body"
                label={t('systemConfig.body')}
                rules={[{ required: true, message: t('systemConfig.bodyRequired') }]}
              >
                <TextArea rows={12} style={{ fontFamily: 'ui-monospace, monospace' }} />
              </Form.Item>
              <Form.Item name="variables" label={t('systemConfig.variables')}>
                <Select
                  mode="tags"
                  tokenSeparators={[',', ' ']}
                  placeholder={t('systemConfig.variablesPlaceholder')}
                  notFoundContent={null}
                />
              </Form.Item>
              <Form.Item name="enabled" label={t('systemConfig.colEnabled')} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Form>
          </>
        ) : null}
      </Drawer>
    </AdminListPage>
  );
}

export default SystemConfig;
